import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  InputBase,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DownloadRounded,
  SlideshowRounded,
} from "@mui/icons-material";

// ---------------------------------------------------------------------------
// Formatting engine (unchanged behaviour — extracted into pure functions so the
// preview and the exported file are always derived from the same output).
// ---------------------------------------------------------------------------

function splitString(str: string, lineLength: Number) {
  let arr = str?.split(" ");
  let result: any = [];
  let subStr = arr[0];
  for (let i = 1; i < arr.length; i++) {
    subStr = subStr.replace(",", ""); //remove comma
    let word = arr[i];
    if (subStr.length + word.length + 1 <= lineLength) {
      subStr = subStr + " " + word;
    } else {
      result.push(subStr);
      subStr = word;
    }
  }
  if (subStr.length) {
    result.push(subStr);
  }

  if (result && result.length > 0) {
    for (var i = 0; i < result.length; i += 2) {
      if (i > 0) {
        result.splice(i, 0, "  ");
      }
    }
  }

  result.push("  ");
  result = result.join("\n");

  return result;
}

// Song-section annotations that belong in lyric sheets but never on a slide,
// e.g. "[Verse 1]", "Chorus", "Pre-Chorus 2:", "(Bridge)", "INTRO". Matches a
// line ONLY when the whole line is a section label (optionally bracketed,
// numbered, or colon-terminated) — so real lyrics containing the word "verse"
// are left untouched.
const SECTION_LABEL = new RegExp(
  "^[\\s>*•\\-]*[\\[(]?\\s*(?:" +
    "pre[\\s-]?chorus|post[\\s-]?chorus|chorus|verse|bridge|intro|outro|" +
    "refrain|hook|pre[\\s-]?hook|tag|vamp|interlude|instrumental|" +
    "break(?:down)?|ending|coda|reprise|turnaround|channel" +
    ")\\s*\\d*\\s*[\\])]?\\s*:?\\s*$",
  "i"
);

function formatLyrics(rawText: string, lineLength: number): string {
  if (!rawText) return "";

  // Keep lines exactly as pasted (minus section labels) so a repeated chorus
  // still lands on slides after every verse — the operator controls repetition.
  const values: any = rawText
    .split("\n")
    .filter((line) => !SECTION_LABEL.test(line));
  let formattedValues: any = [];

  values.forEach((textLine: string, index: any) => {
    const previousLine = formattedValues[index - 1];
    let formattedLine = textLine;
    let hasNoSpace = previousLine && !previousLine?.includes("\n");

    ///remove brackets from line
    if (textLine && textLine?.length > 0) {
      formattedLine = textLine.replace(/\s*\(.*?\)\s*/g, "");
      if (formattedLine?.length > lineLength) {
        formattedLine = splitString(formattedLine, lineLength);
        if (hasNoSpace) {
          formattedLine = "\n" + formattedLine;
        }
      } else {
        hasNoSpace =
          previousLine &&
          previousLine?.length < lineLength &&
          !previousLine?.includes("\n");
        if (hasNoSpace) {
          const currArrayLine = formattedLine?.split(" ");
          currArrayLine?.push("\n");
          formattedLine = currArrayLine?.join(" ");
        }
      }
    }

    formattedValues.push(formattedLine);
  });

  formattedValues = formattedValues.filter(Boolean); ///remove spaces
  return formattedValues.join("\n");
}

// Group the formatted output into slides exactly the way ProPresenter will when
// it imports the .txt: consecutive non-empty lines form a slide, blank /
// whitespace-only lines are slide boundaries. Preview === exported result.
function splitIntoSlides(formatted: string): string[][] {
  if (!formatted || !formatted.trim()) return [];
  const slides: string[][] = [];
  let current: string[] = [];

  formatted.split("\n").forEach((line) => {
    if (line.trim() === "") {
      if (current.length) {
        slides.push(current);
        current = [];
      }
    } else {
      current.push(line.trimEnd());
    }
  });
  if (current.length) slides.push(current);

  return slides;
}

// ---------------------------------------------------------------------------

// Each panel is a self-contained, definite-height box that scrolls its own
// content. A definite height (px / vh) is what makes the inner `flex:1 +
// minHeight:0 + overflow:auto` scroll region resolve reliably — percentage
// heights through a MUI Grid do not. On desktop it tracks the viewport (so the
// editor/preview fill the first screen) but never drops below 360px; on mobile
// it's a fixed 360px and the page scrolls. The FAQ always sits cleanly below.
const PANEL_H = { xs: 360, md: "max(360px, calc(100dvh - 210px))" };

const panelSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: "14px",
  bgcolor: "background.paper",
  boxShadow: "0 1px 2px rgba(24,24,27,0.04)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
} as const;

export default function Home({
  rawText,
  setRawText,
}: {
  rawText: any;
  setRawText: any;
}) {
  const [lineLength, setLineLength] = React.useState(35);
  const [savingFile, setSavingFile] = React.useState(false);
  const [fileName, setFileName] = React.useState("");

  // Live formatting — paste/type/drag the slider and the preview updates
  // instantly. No "Format" click required.
  const formatText = React.useMemo(
    () => formatLyrics(rawText, lineLength),
    [rawText, lineLength]
  );
  const slides = React.useMemo(() => splitIntoSlides(formatText), [formatText]);
  const hasOutput = slides.length > 0;

  // Suggest a file name from the first lyric line (there's no song title to
  // pull from). Editable — selected on focus so typing overwrites it.
  const suggestedName = React.useMemo(() => {
    const firstLine = slides[0]?.[0] ?? "";
    return firstLine
      .replace(/\s+/g, " ")
      .replace(/["'’“”]/g, "")
      .replace(/[.,;:!?-]+$/g, "")
      .trim()
      .slice(0, 60);
  }, [slides]);

  const handleChange = (event: any) => setRawText(event.target.value);
  const handleSliderChange = (_event: any, newValue: any) =>
    setLineLength(newValue);

  const openSaveModal = () => {
    setFileName(suggestedName);
    setSavingFile(true);
  };
  const closeSaveModal = () => {
    setFileName("");
    setSavingFile(false);
  };

  const download = () => {
    const blob = new Blob([formatText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = fileName.trim() || suggestedName || "Lyrics";
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    closeSaveModal();
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 5 } }}
    >
      {/* The page's <h1> + value proposition now live in the sticky header
          (see NavBar) so the editor/preview get the full screen here. -------- */}

      {/* Control bar: line length + slide count + export ------------------- */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: { xs: 2, md: 3 },
          mb: { xs: 2.5, md: 3 },
        }}
      >
        <Box sx={{ flex: { xs: "1 1 100%", sm: "0 1 320px" }, minWidth: 240 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              component="label"
              id="line-length-label"
              sx={{ color: "text.secondary" }}
            >
              Line length
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
            >
              {lineLength}
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {" "}
                chars
              </Box>
            </Typography>
          </Box>
          <Slider
            value={lineLength}
            onChange={handleSliderChange}
            min={10}
            max={60}
            valueLabelDisplay="auto"
            aria-labelledby="line-length-label"
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            icon={<SlideshowRounded sx={{ fontSize: 18 }} />}
            label={`${slides.length} ${slides.length === 1 ? "slide" : "slides"}`}
            variant="outlined"
            sx={{
              fontWeight: 600,
              borderColor: "divider",
              bgcolor: "background.paper",
              "& .MuiChip-icon": { color: "secondary.light" },
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            size="large"
            disabled={!hasOutput}
            onClick={openSaveModal}
            startIcon={<DownloadRounded />}
          >
            Export .txt
          </Button>
        </Stack>
      </Box>

      {/* Editor + preview --------------------------------------------------- */}
      <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="stretch">
        {/* Paste / editor */}
        <Grid item xs={12} md={6}>
          <Box sx={{ ...panelSx, height: PANEL_H }}>
            <PanelHeader
              title="Lyrics or scripture"
              caption="Paste below — it formats as you go"
            />
            <Divider />
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2.5 }}>
              <InputBase
                fullWidth
                multiline
                value={rawText}
                onChange={handleChange}
                placeholder="Paste song lyrics or Bible verses here…"
                inputProps={{ "aria-label": "Lyrics or scripture input" }}
                sx={{
                  height: "100%",
                  alignItems: "flex-start",
                  fontSize: 15,
                  lineHeight: 1.7,
                  color: "text.primary",
                  "& textarea": { height: "100% !important", overflow: "auto !important" },
                  "& textarea::placeholder": { color: "text.disabled", opacity: 1 },
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Slide preview */}
        <Grid item xs={12} md={6}>
          <Box sx={{ ...panelSx, height: PANEL_H }}>
            <PanelHeader
              title="ProPresenter preview"
              caption={hasOutput ? "This is exactly what will export" : undefined}
            />
            <Divider />
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                p: { xs: 2, md: 2.5 },
                bgcolor: "background.default",
              }}
            >
              {hasOutput ? (
                <Stack spacing={1.5}>
                  {slides.map((lines, i) => (
                    <SlideCard key={i} index={i + 1} lines={lines} />
                  ))}
                </Stack>
              ) : (
                <EmptyPreview />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Indexable long-form content — invisible to the workflow but the
          substance search engines (and first-time church visitors) read to
          understand what ProText is and rank it for relevant queries. ------- */}
      <InfoContent />

      {/* Export dialog ------------------------------------------------------ */}
      <Dialog
        open={savingFile}
        onClose={closeSaveModal}
        fullWidth
        maxWidth="xs"
        aria-labelledby="export-dialog-title"
      >
        <DialogTitle id="export-dialog-title" sx={{ fontWeight: 700, pb: 0.5 }}>
          Export to ProPresenter
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saved as a plain-text (.txt) file you can import directly into
            ProPresenter.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="File name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === "Enter") download();
            }}
            placeholder="Lyrics"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    .txt
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={closeSaveModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={download}
            variant="contained"
            color="secondary"
            startIcon={<DownloadRounded />}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// --- Small presentational pieces ------------------------------------------

function PanelHeader({ title, caption }: { title: string; caption?: string }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 2,
        flexShrink: 0,
      }}
    >
      <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
        {title}
      </Typography>
      {caption ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function SlideCard({ index, lines }: { index: number; lines: string[] }) {
  return (
    <Box
      sx={{
        position: "relative",
        border: 1,
        borderColor: "divider",
        borderRadius: "10px",
        bgcolor: "background.paper",
        px: 3,
        py: 2.5,
        minHeight: 76,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: 8,
          left: 12,
          color: "text.disabled",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {index}
      </Typography>
      {lines.map((line, i) => (
        <Typography
          key={i}
          sx={{
            fontSize: { xs: 16, md: 18 },
            fontWeight: 500,
            lineHeight: 1.5,
            color: "text.primary",
          }}
        >
          {line.trim() === "" ? " " : line}
        </Typography>
      ))}
    </Box>
  );
}

const STEPS = [
  {
    title: "1. Paste lyrics or Bible verses",
    body: "Copy the lyrics for any worship song — or the text of a Bible passage — and paste them into the editor. ProText strips out section labels like “[Verse 1]” or “Chorus” automatically so only the words that belong on screen remain.",
  },
  {
    title: "2. Set the line length",
    body: "Drag the slider to match how much text fits comfortably on your church's screens. ProText re-wraps every line and groups them into slides live, so the preview always matches what ProPresenter will show.",
  },
  {
    title: "3. Export ProPresenter-ready slides",
    body: "Click Export to download a plain-text (.txt) file. Import it straight into ProPresenter and each block becomes its own slide — no manual line breaks, no cleanup.",
  },
];

const FAQS = [
  {
    q: "Is ProText free to use?",
    a: "Yes. ProText is completely free, with no account or sign-up required. Paste your text, format, and export as many songs or passages as you like.",
  },
  {
    q: "Can I format Bible verses too?",
    a: "Yes. ProText works for any text you put on screen, not just lyrics. Paste a Bible passage and it wraps the lines and splits it into slides the same way, ready to import into ProPresenter.",
  },
  {
    q: "How do I import the file into ProPresenter?",
    a: "In ProPresenter, create or open a presentation and import the exported .txt file (or drag it onto the library). ProPresenter reads each blank-line-separated block as a new slide, which is exactly how ProText formats the output.",
  },
  {
    q: "Do I need to install anything?",
    a: "No. ProText runs entirely in your web browser on any computer, so there's nothing to download or install. It works on Mac and Windows alongside ProPresenter.",
  },
  {
    q: "Will the slides match my screen size?",
    a: "Use the line-length slider to control how many characters appear per line. The live preview mirrors the exported result, so you can dial in a length that fits your auditorium displays before exporting.",
  },
];

function InfoContent() {
  return (
    <Box
      component="section"
      aria-label="About ProText"
      sx={{ maxWidth: 820, mt: { xs: 6, md: 9 } }}
    >
      <Typography
        component="h2"
        sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, mb: 2.5 }}
      >
        How ProText works
      </Typography>
      <Stack spacing={2.5} sx={{ mb: { xs: 5, md: 7 } }}>
        {STEPS.map((step) => (
          <Box key={step.title}>
            <Typography
              component="h3"
              sx={{ fontSize: 17, fontWeight: 600, mb: 0.5, color: "text.primary" }}
            >
              {step.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
              {step.body}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Typography
        component="h2"
        sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, mb: 2.5 }}
      >
        Frequently asked questions
      </Typography>
      <Stack spacing={2.5}>
        {FAQS.map((item) => (
          <Box key={item.q}>
            <Typography
              component="h3"
              sx={{ fontSize: 17, fontWeight: 600, mb: 0.5, color: "text.primary" }}
            >
              {item.q}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
              {item.a}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function EmptyPreview() {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
        px: 3,
      }}
    >
      <SlideshowRounded sx={{ fontSize: 40, color: "text.disabled" }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
        Your slides will appear here
      </Typography>
      <Typography variant="caption" sx={{ color: "text.disabled", maxWidth: 240 }}>
        Paste lyrics on the left to see the ProPresenter-ready preview.
      </Typography>
    </Box>
  );
}
